import { useEffect, useRef } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { ANALYSES, analysisName } from "./constants";
import QueueItem from "./QueueItem";

/*
 * The review list: Flagged (the default) or Every check, narrowed to one
 * analysis from a lane, sorted by time or with flags first. An item opens to
 * its evidence.
 */
export default function ReviewQueue({
  queue, allCount, flaggedCount, scope, onScope, filter, onFilter, order, onOrder,
  sel, liveKeys, moduleNameAt, thumbOf,
  onToggle, onImageError, renderEvidence, countFor,
}) {
  const listRef = useRef(null);

  /* bring the open item into view when it changes (a pick from the lanes, the player or the keys) */
  useEffect(() => {
    if (!sel || !listRef.current) return;
    const node = Array.from(listRef.current.querySelectorAll("[data-key]")).find((n) => n.getAttribute("data-key") === sel);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [sel]);

  /* a name mid-sentence: "Image" → "image", an initialism like "AI use" stays */
  const inSentence = (n) => (/^[A-Z]{2}/.test(n) ? n : n.toLowerCase());
  const filterName = filter !== "any" ? (ANALYSES.find((a) => a.k === filter) || {}).short : null;

  return (
    <main className="proctoring-review__left" aria-label="Review list">
      <div className="proctoring-review__q-head">
        <div className="proctoring-review__seg" role="tablist" aria-label="What to list">
          <button type="button" role="tab" aria-selected={scope === "flagged"} className={scope === "flagged" ? "is-on" : ""} onClick={() => onScope("flagged")}>
            {`Flagged ${flaggedCount}`}
          </button>
          <button type="button" role="tab" aria-selected={scope === "all"} className={scope === "all" ? "is-on" : ""} onClick={() => onScope("all")}>
            {`Every check ${allCount}`}
          </button>
        </div>
        {filterName && (
          <button type="button" className="proctoring-review__chip-btn" onClick={() => onFilter("any")} aria-label={`Show every analysis, not only ${filterName}`}>
            {filterName} <CloseOutlined />
          </button>
        )}
        <span className="proctoring-review__grow" />
        {scope === "all" && (
          <label className="proctoring-review__sort">
            Sort
            <select value={order} onChange={(e) => onOrder(e.target.value)}>
              <option value="time">By time</option>
              <option value="severity">Flagged first</option>
            </select>
          </label>
        )}
      </div>

      {!queue.length && (
        <div className="proctoring-review__empty">
          {scope === "flagged"
            ? (
              <>
                <b>Nothing to review</b>
                <span>{filterName ? `Every ${inSentence(filterName)} check came back clear.` : "Every check came back clear."}</span>
                <button type="button" onClick={() => onScope("all")}>{`See all ${countFor(filter)} checks`}</button>
              </>
            )
            : <span>Nothing recorded for this analysis.</span>}
        </div>
      )}

      <ol className="proctoring-review__list" ref={listRef}>
        {queue.map((x) => {
          const open = x.key === sel;
          return (
            <QueueItem
              key={x.key}
              x={x}
              open={open}
              live={liveKeys.has(x.key)}
              analysis={analysisName(x.kind)}
              where={moduleNameAt(x.at)}
              thumbUrl={x.kind === "capture" || x.kind === "screen" ? thumbOf(x) : null}
              onToggle={onToggle}
              onImageError={onImageError}
            >
              {open ? renderEvidence(x) : null}
            </QueueItem>
          );
        })}
      </ol>
      <p className="proctoring-review__foot">
        <kbd>J</kbd><kbd>K</kbd> move · <kbd>Space</kbd> play · <kbd>Enter</kbd> enlarge · <kbd>Esc</kbd> close
      </p>
    </main>
  );
}
